import React from 'react'
import Redirect from '#src/components/Redirect'
import { FancyModal, FullBlockLoader, CircleLoader } from '@deal/components'
import { withAnalytics, WithAnalyticsProps } from '#src/containers/Analytics'
import config from '#src/config'
import cookies from 'js-cookie'
import { Experiment } from '@deal/experiment-js'
import {
  generateCreateUrlFromCategory,
  getOnboardingPathSlug,
  generateCreateUrl
} from '#src/services/path'
import V1 from './V1'
import { EngagementChannelCategoryFragment } from '#src/generated/graphql'
import styles from './styles.css'

interface EngagementModalProps extends WithAnalyticsProps {
  category: EngagementChannelCategoryFragment
}

interface EnagementModalState {
  redirect?: string
  isEngagementModalOpen: boolean
  isTeaserChatOpen: boolean
}

class EngagementModal extends React.Component<EngagementModalProps, EnagementModalState> {
  constructor(props: EngagementModalProps) {
    super(props)

    this.state = {
      isEngagementModalOpen: false,
      isTeaserChatOpen: false
    }

    this.handleCloseModal = this.handleCloseModal.bind(this)
    this.handleSubmitModal = this.handleSubmitModal.bind(this)
  }

  public componentDidMount() {
    const hasEngagementModalBeenViewed = cookies.get('engagement_modal_viewed')

    if (!hasEngagementModalBeenViewed) {
      setTimeout(() => {
        this.setState({
          isEngagementModalOpen: true
        })
      }, 2000)
    }
  }

  public handleCloseModal(trackDismiss: boolean, setCookie: boolean) {
    this.setState(
      {
        isEngagementModalOpen: false,
        isTeaserChatOpen: true
      },
      () => {
        if (setCookie) {
          cookies.set('engagement_modal_viewed', 'true', {
            expires: 1,
            domain: config.get('consumer.clientSideCookieDomain')
          })
        }

        if (this.props.analytics && trackDismiss) {
          this.props.analytics.engagementModalDismissed()
        }
      }
    )
  }

  public handleSubmitModal(categorySlug: string | null) {
    if (this.props.analytics) {
      this.props.analytics.engagementModalSubmitted()
    }

    this.setState({
      redirect: categorySlug
        ? generateCreateUrlFromCategory(categorySlug)
        : generateCreateUrl(getOnboardingPathSlug())
    })
  }

  public render() {
    if (this.state.redirect) {
      return <Redirect to={this.state.redirect} />
    }

    const { category } = this.props

    const pathCategorySlug = category.ancestorWithPath ? category.ancestorWithPath.slug : null
    const hasEngagementModalBeenViewed = cookies.get('engagement_modal_viewed')

    // No path category or path available, or seen already, then return null
    if (!pathCategorySlug || hasEngagementModalBeenViewed) {
      return null
    }

    return (
      <FancyModal
        isOpen={this.state.isEngagementModalOpen}
        onRequestClose={() => this.handleCloseModal(true, true)}
        className={styles.modal}
        modalContentClassName={styles.modalContent}
      >
        <Experiment id="engagement-modal-variations" default="v1">
          {({ result, loading }) => {
            if (loading) {
              return <FullBlockLoader loader={CircleLoader} />
            }

            /**
             * The result will be `v1`, `v2`, etc. We use that to build a contentful variation slug
             * that we will pass into the variation component: golf-v1
             */
            const contentfulVariationSlug = `${pathCategorySlug}-${result}`

            switch (result) {
              case 'v1':
                return (
                  <V1
                    contentfulVariationSlug={contentfulVariationSlug}
                    category={category}
                    onDismissModal={this.handleCloseModal}
                    onSubmitModal={() => this.handleSubmitModal(pathCategorySlug)}
                  />
                )

              default:
                return null
            }
          }}
        </Experiment>
      </FancyModal>
    )
  }
}

export default withAnalytics(EngagementModal)
